import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import SEO from '../components/SEO';
import './PrivacyPolicyPage.css';

// To update this page, edit the `sections` array below.
// Each section has a `title` and a `content` array of paragraphs.
// You can also add bullet lists by using { type: 'list', items: [...] }.
const LAST_UPDATED = 'April 2026';

const sections = [
  {
    title: '1. Who We Are',
    content: [
      'DiveConnect ("we", "us", or "our") operates the website at diveconnect.com. We provide a platform for discovering and booking scuba diving trips worldwide.',
      'If you have any questions about this policy, contact us at privacy@dive-connect.com.',
    ],
  },
  {
    title: '2. Information We Collect',
    content: [
      'We collect information you provide directly when you create an account, book a trip, or contact us:',
      {
        type: 'list',
        items: [
          'Name and email address',
          'Diving certification level and agency',
          'Profile information you choose to add',
          'Trip application details',
          'Payment information (processed securely by our payment provider — we do not store card details)',
        ],
      },
      'We also collect certain technical information automatically when you use our site, including your IP address, browser type, pages visited, and referring URLs. This is used solely to improve the platform.',
    ],
  },
  {
    title: '3. How We Use Your Information',
    content: [
      'We use your information to:',
      {
        type: 'list',
        items: [
          'Create and manage your account',
          'Process trip applications and send booking confirmations',
          'Send transactional emails (e.g. password resets, booking updates)',
          'Send our newsletter, if you have subscribed',
          'Improve the platform and fix issues',
          'Comply with legal obligations',
        ],
      },
      'We will never sell your personal data to third parties.',
    ],
  },
  {
    title: '4. Cookies',
    content: [
      'We use essential cookies to keep you logged in and remember your preferences. We do not use third-party advertising cookies.',
      'You can disable cookies in your browser settings, but some parts of the site may not function correctly without them.',
    ],
  },
  {
    title: '5. Sharing Your Information',
    content: [
      'We share your information only in the following limited circumstances:',
      {
        type: 'list',
        items: [
          'With trip organisers, to process your application (name, certification details, and contact email only)',
          'With our email service provider, to send transactional and newsletter emails',
          'With our payment processor, to handle transactions securely',
          'If required by law or to protect the rights and safety of our users',
        ],
      },
    ],
  },
  {
    title: '6. Data Retention',
    content: [
      'We retain your account data for as long as your account is active. If you delete your account, we will remove your personal data within 30 days, except where we are required to retain it for legal or accounting purposes.',
    ],
  },
  {
    title: '7. Your Rights',
    content: [
      'Depending on your location, you may have the right to:',
      {
        type: 'list',
        items: [
          'Access the personal data we hold about you',
          'Request correction of inaccurate data',
          'Request deletion of your data',
          'Object to or restrict how we process your data',
          'Receive a copy of your data in a portable format',
        ],
      },
      'To exercise any of these rights, email us at privacy@dive-connect.com. We will respond within 30 days.',
    ],
  },
  {
    title: '8. Security',
    content: [
      'We take reasonable technical and organisational measures to protect your data against unauthorised access, loss, or misuse. All data is transmitted over HTTPS.',
      'No online service can guarantee absolute security. If you believe your account has been compromised, contact us immediately.',
    ],
  },
  {
    title: '9. Children\'s Privacy',
    content: [
      'Our platform is not intended for users under the age of 16. We do not knowingly collect personal data from children. If you believe we have inadvertently collected such data, please contact us and we will delete it promptly.',
    ],
  },
  {
    title: '10. Changes to This Policy',
    content: [
      'We may update this Privacy Policy from time to time. When we do, we will update the "Last Updated" date at the top of this page and, where appropriate, notify you by email.',
      'Continued use of DiveConnect after changes are posted constitutes your acceptance of the updated policy.',
    ],
  },
];

const PrivacyPolicyPage = () => {
  return (
    <div className="privacy-page">
      <SEO
        title="Privacy Policy - DiveConnect"
        description="Read the DiveConnect Privacy Policy to understand how we collect, use, and protect your personal data."
      />

      {/* Hero */}
      <div className="privacy-hero">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto text-center">
              <h1 className="privacy-hero-title">Privacy Policy</h1>
              <p className="privacy-hero-subtitle">Last updated: {LAST_UPDATED}</p>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Content */}
      <section className="privacy-content">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto">
              <p className="privacy-intro">
                Your privacy matters to us. This policy explains what personal information
                DiveConnect collects, how we use it, and what rights you have over your data.
                Please read it carefully.
              </p>

              {sections.map((section) => (
                <div key={section.title} className="privacy-section">
                  <h2 className="privacy-section-title">{section.title}</h2>
                  {section.content.map((block, i) =>
                    typeof block === 'string' ? (
                      <p key={i} className="privacy-text">{block}</p>
                    ) : (
                      <ul key={i} className="privacy-list">
                        {block.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    )
                  )}
                </div>
              ))}

              <div className="privacy-contact">
                <h2 className="privacy-section-title">Contact Us</h2>
                <p className="privacy-text">
                  If you have any questions about this Privacy Policy or how we handle your data,
                  please email us at{' '}
                  <a href="mailto:privacy@dive-connect.com">privacy@dive-connect.com</a>.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
